import CashCount from 'src/models/cash/CashCount'
import CashSession from 'src/models/cash/CashSession'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import Hardware from 'src/models/hardware/Hardware'
import Model, { encoders, ID, Integer, IsoDateTime, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import PaymentConnectorConfiguration from 'src/models/payment-connector/PaymentConnectorConfiguration'
import RegisterType from 'src/models/register/RegisterType'

export const CASH_COUNT_INITIALIZE_OPTIONS = {
  NEW: 'new',
  CONTINUE: 'continue'
}
export const POS_PAYMENT_TYPES = {
  WINDCAVE: 'windcave',
  STRIPE: 'stripe',
  EVERTEC: 'evertec',
  AZUL: 'azul'
}
export const PRINTER_TYPES = {
  EPSON: 'epson',
  STAR: 'star'
}
PRINTER_TYPES.ALL = _.values(PRINTER_TYPES)
export const BARCODE_TYPES = {
  QR_CODE: 'qr-code',
  BARCODE: 'barcode'
}
BARCODE_TYPES.ALL = _.values(BARCODE_TYPES)

export const CASH_DRAWER_EVENTS = {
  CONNECT: 'Connect',
  OPEN: 'Open',
  DEVICE_LIST: 'DeviceList',
  STATUS: 'Status'
}
CASH_DRAWER_EVENTS.ALL = _.values(CASH_DRAWER_EVENTS)

export const CASH_DRAWER_TYPES = {
  USB: 'usb',
  SCRIPT: 'script'
}
CASH_DRAWER_TYPES.ALL = _.values(CASH_DRAWER_TYPES)

export default class RegisterStation extends Model {
  static name = 'RegisterStation'

  static isConfiguration = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.HardwareAndRegisterStation]

  static define () {
    this.concerns = [Archivable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        default: null
      },
      siteId: {
        type: ID,
        output: true
      },
      registerType: {
        type: RegisterType,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      automaticCashSessionsCashTrayId: {
        type: ID,
        default: null
      },
      hardwareId: {
        type: ID,
        default: null
      },
      hardware: {
        type: Hardware,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      printerType: {
        type: String,
        default: PRINTER_TYPES.EPSON
      },
      barcodeType: {
        type: String,
        default: BARCODE_TYPES.QR_CODE
      },
      electronReceiptPaperWidth: {
        type: Integer,
        default: 576
      },
      displayTimeInHeader: {
        type: Boolean,
        default: false
      },
      paymentConnectorMaintenanceOptions: {
        type: JsonArray,
        output: true,
        default () {
          return []
        }
      },
      paymentConnectorConfigurations: {
        type: [PaymentConnectorConfiguration],
        acceptsNestedAttributes: true,
        excludeFromDefault: true
      },
      indyVersion: {
        type: String,
        default: null,
        excludeFromDefault: true,
        output: true
      },
      electronVersion: {
        type: String,
        default: null,
        excludeFromDefault: true,
        output: true
      },
      lastOnline: {
        type: IsoDateTime,
        default: null,
        excludeFromDefault: true,
        output: true
      },
      enabledPaymentConnectorConfiguration: {
        type: JsonObject,
        default: null,
        output: true
      },
      oposConfiguration: {
        type: JsonObject,
        default: () => ({
          cashdrawer_enabled: false,
          cashdrawer_device_id: null,
          cashdrawer_type: CASH_DRAWER_TYPES.USB,
          cashdrawer_command_script: null
        }),
        mergeDefault: true
      },

      activeCashCount: {
        type: CashCount,
        output: true,
        default: null
      },
      activeCashSession: {
        type: CashSession,
        output: true,
        default: null
      },
      lastCashSession: {
        type: CashSession,
        output: true,
        default: null
      },

      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      useStripeTerminalPayments () {
        if (this.enabledPaymentConnectorConfiguration) {
          return this.enabledPaymentConnectorConfiguration.name === POS_PAYMENT_TYPES.STRIPE
        } else {
          return true
        }
      },
      inventoryStrategyEntityDisplayName () {
        return `${this.name} (${this.humanTypeName})`
      }
    }
  }

  // Some payment connectors are also used for fiscal requirements.
  // This is used to determine if cash payments should be processed through the payment connector.
  get paymentConnectorRequiredForCash () {
    return !!this.enabledPaymentConnectorConfiguration?.configuration?.required_for_cash_payments
  }

  // Some payment connectors are also used for fiscal requirements. and require a unique transaction number for each transaction
  get paymentConnectorRequiresTransactionNumber () {
    return !!this.enabledPaymentConnectorConfiguration?.configuration?.require_transaction_number
  }

  // Due to tax requirements, some payment connectors require that the payment be processed by order item.
  // This is to avoid the complexity of having to split tax across multiple items.
  get paymentConnectorRequirePaymentByOrderItem () {
    return !!this.enabledPaymentConnectorConfiguration?.configuration?.require_payment_by_order_item
  }

  // Due to tax requirements, some payment connectors require that the refund be processed by order item.
  // This is to avoid the complexity of having to split tax across multiple items.
  get paymentConnectorRequireRefundByOrderItem () {
    return !!this.enabledPaymentConnectorConfiguration?.configuration?.require_refund_by_order_item
  }

  get paymentConnectorName () {
    return this.enabledPaymentConnectorConfiguration?.name || POS_PAYMENT_TYPES.STRIPE
  }

  get defaultStripeReaderSerialNumber () {
    return this.enabledPaymentConnectorConfiguration?.configuration?.default_stripe_reader_serial_number
  }

  // Some connectors have different transaction types, e.g. Foodstamp Cards, etc.
  get paymentConnectorOtherTransactionTypes () {
    return this.enabledPaymentConnectorConfiguration?.configuration?.other_transaction_types || []
  }

  // Some connectors accept different transaction options, e.g. Force, Tip, etc.
  get paymentConnectorTransactionOptions () {
    return this.enabledPaymentConnectorConfiguration?.transaction_options || []
  }

  get deliveryTypeOptions () {
    const options = []

    if (
      this.configuration.currentSite.counterPickupEnabled &&
              (
                this.registerType
                  ? this.registerType.showCounterPickupOption
                  : this.configuration.currentSite.counterPickupAvailableOnline
              )
    ) {
      options.push({
        value: DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP,
        label: _.startCase('counter-pickup')
      })
    }
    if (
      this.configuration.currentSite.expressPickupEnabled &&
              (
                this.registerType
                  ? this.registerType.showExpressPickupOption
                  : this.configuration.currentSite.expressPickupAvailableOnline
              )
    ) {
      options.push({
        value: DELIVERY_TYPE_OPTIONS.EXPRESS_PICKUP,
        label: _.startCase('express-pickup')
      })
    }
    if (
      this.configuration.currentSite.theaterSeatDeliveryEnabled &&
              (
                this.registerType
                  ? this.registerType.showTheaterSeatDeliveryOption
                  : this.configuration.currentSite.theaterSeatDeliveryAvailableOnline
              )
    ) {
      options.push({
        value: DELIVERY_TYPE_OPTIONS.THEATER_SEAT,
        label: _.startCase('theater-seat')
      })
    }
    if (
      this.configuration.currentSite.tableDeliveryEnabled &&
              (
                this.registerType
                  ? this.registerType.showTableDeliveryOption
                  : this.configuration.currentSite.tableDeliveryAvailableOnline
              )
    ) {
      options.push({
        value: DELIVERY_TYPE_OPTIONS.TABLE,
        label: _.startCase('table')
      })
    }

    _.each(this.registerType.deliveryTypes, deliveryTypeId => {
      const customDeliveryType = _.find(this.configuration.deliveryTypes.data, deliveryType => deliveryType.id === deliveryTypeId)

      if (customDeliveryType && customDeliveryType.enable) options.push(customDeliveryType)
    })

    // Filter to reflect the priority sort order
    const deliveryTypePriority = this.registerType.deliveryTypePriority

    const prioritisedDeliveryTypeOptions = []

    // ADD THE PRIORITISED DELIVERY TYPES
    prioritisedDeliveryTypeOptions.push(
      _.filter(options, ({ value }) => !!_.find(deliveryTypePriority, (type) => value === type))
    )

    // ADD THE REST
    prioritisedDeliveryTypeOptions.push(
      _.filter(options, ({ value }) => !_.find(deliveryTypePriority, (type) => value === type))
    )

    return _.flatten(prioritisedDeliveryTypeOptions)
  }

  get isOposCashdrawerEnabled () {
    return this.oposConfiguration.cashdrawer_enabled
  }

  set isOposCashdrawerEnabled (enabled) {
    this.oposConfiguration.cashdrawer_enabled = enabled
  }

  get oposCashDrawerType () {
    return this.oposConfiguration.cashdrawer_type
  }

  set oposCashDrawerType (enabled) {
    this.oposConfiguration.cashdrawer_type = enabled
  }

  get cashdrawerCommandScript () {
    return this.oposConfiguration.cashdrawer_command_script
  }

  set cashdrawerCommandScript (script) {
    this.oposConfiguration.cashdrawer_command_script = script
  }

  get cashdrawerDeviceId () {
    return this.oposConfiguration.cashdrawer_device_id
  }

  async setCashdrawerDeviceId (deviceId) {
    this.oposConfiguration = {
      ...this.oposConfiguration,
      cashdrawer_device_id: deviceId
    }

    await this.save()
  }

  async getCashdrawerList () {
    if (!window.electronOPosCashdrawer) {
      return 'Unable to find cashdrawer module.'
    }

    try {
      window.electronOPosCashdrawer.listAll()
    } catch (error) {
      return `Unable to get cashdrawer list. error: ${error.message}`
    }
  }

  async connectToCashDrawerById (id) {
    if (!window.electronOPosCashdrawer) {
      return 'Unable to find cashdrawer module.'
    }

    try {
      window.electronOPosCashdrawer.connectById(id)
    } catch (error) {
      return `Unable to connect to cashdrawer. error: ${error.message}`
    }
  }

  async connectToFirstCashDrawer () {
    if (!window.electronOPosCashdrawer) {
      return 'Unable to find cashdrawer module.'
    }

    try {
      window.electronOPosCashdrawer.connectFirst()
    } catch (error) {
      return `Unable to connect to cashdrawer. error: ${error.message}`
    }
  }

  async openCashDrawer () {
    try {
      if (this.oposCashDrawerType === CASH_DRAWER_TYPES.USB && !window.electronOPosCashdrawer) {
        return 'Unable to find cashdrawer module.'
      } else if (this.oposCashDrawerType === CASH_DRAWER_TYPES.USB) {
        window.electronOPosCashdrawer.open()
      } else if (this.oposCashDrawerType === CASH_DRAWER_TYPES.SCRIPT && !window.electronExecuteCommand) {
        return 'Unable to find command execution module.'
      } else if (this.oposCashDrawerType === CASH_DRAWER_TYPES.SCRIPT) {
        window.electronExecuteCommand(this.cashdrawerCommandScript)
      }
    } catch (error) {
      return `Unable to open cashdrawer. error: ${error.message}`
    }
  }

  async subscribeToCashdrawerEvent (eventName, callback) {
    if (!window.electronOPosCashdrawer) {
      return 'Unable to find cashdrawer module.'
    }

    try {
      const subscribeEvent = window.electronOPosCashdrawer[`subscribe${eventName}`]
      if (!subscribeEvent) {
        return `Cashdrawer event: ${eventName} is invalid. Available options: ${CASH_DRAWER_EVENTS.ALL.join(', ')}`
      }
      subscribeEvent(callback)
    } catch (error) {
      return `Unable to subscribe to cashdrawer event ${eventName}. error: ${error.message}`
    }
  }

  async requestCashdrawerStatus () {
    if (!window.electronOPosCashdrawer) {
      return 'Unable to find cashdrawer module.'
    }

    try {
      window.electronOPosCashdrawer.status()
    } catch (error) {
      return `Unable to request cashdrawer status. error: ${error.message}`
    }
  }

  async createAttachedCashTray () {
    this.action = 'create_attached_cash_tray'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
